/**
 * Parse Profit Plus `syncedAt` from Firestore / API.
 * Client SDK uses Timestamp with `toDate()`; REST/JSON often returns `{ seconds, nanoseconds }`.
 */

/** Allow server time slightly behind client `Date.now()` when comparing to poll start. */
export const PROFIT_SYNC_TIMESTAMP_SKEW_MS = 120_000;

export function profitPlusTimestampToMs(value: unknown): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as {
      toDate?: () => Date;
      seconds?: number;
      nanoseconds?: number;
    };
    if (typeof o.toDate === 'function') {
      try {
        const d = o.toDate();
        if (d instanceof Date) {
          const ms = d.getTime();
          return Number.isFinite(ms) ? ms : null;
        }
      } catch {
        /* fall through */
      }
    }
    if (typeof o.seconds === 'number' && Number.isFinite(o.seconds)) {
      const ns = typeof o.nanoseconds === 'number' ? o.nanoseconds : 0;
      return o.seconds * 1000 + Math.floor(ns / 1e6);
    }
    const p = value as { _seconds?: number; _nanoseconds?: number };
    if (typeof p._seconds === 'number' && Number.isFinite(p._seconds)) {
      const ns = typeof p._nanoseconds === 'number' ? p._nanoseconds : 0;
      return p._seconds * 1000 + Math.floor(ns / 1e6);
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

/** Read `syncedAt` from a `profitPlus2k8` blob (camelCase + snake_case). */
export function profitPlusSyncedAtMsFromIntegration(pp: unknown): number | null {
  if (pp == null || typeof pp !== 'object') return null;
  const o = pp as Record<string, unknown>;
  return (
    profitPlusTimestampToMs(o.syncedAt) ??
    profitPlusTimestampToMs(o.synced_at)
  );
}
